/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Collapse,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
} from '@material-ui/core';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { parseDateTime } from 'utils/datetime';
import ConsumptionsReportForm from 'forms/ConsumptionsReportForm';
import { getAllReportingOrders, getReportingOrdersSummary } from 'services/orderServices';
import { getPaymentIdsByMethod } from 'services/paymentsServices';
import PaginationData from 'types/models/PaginationData';
import Order from 'types/models/Order';
import { ConsumptionReportPayloadData } from 'types/forms/ConsumptionsReportFormProps';
import OrderDetail from 'types/models/OrderDetail';
import OrderBillingStatuses from 'types/enums/OrderBillingStatuses';
import ByMethodPaymentResult from 'types/models/ByMethodPaymentResult';
import { parsePaymentMethod, parseSplitPaymentText } from 'utils/report';
import { formatPhone } from 'utils/formatPhone';
import { formatMoneyAuto } from 'utils/formatMoney';
import BooleanIcon from 'components/common/BooleanIcon';
import AppContext from 'contexts/AppContext';
import 'styles/pages/ConsumptionsReportPage.scss';

// Definimos la interfaz del resumen devuelto por el Backend
interface SummaryData {
  total_transactions: number;
  total_amount: number;
}

export default (): JSX.Element => {
  const { t, i18n } = useTranslation();

  const { setIsLoading, isLoading } = React.useContext(AppContext);
  const [currentFormData, setCurrentFormData] = React.useState<ConsumptionReportPayloadData | null>(null);

  // Estado para el resumen de montos y transacciones
  const [summary, setSummary] = React.useState<SummaryData | null>(null);
  const [paginationOrders, setPaginationOrders] = React.useState<PaginationData<Order> | null>(null);
  const [openCollapseDetail, setOpenCollapseDetail] = React.useState(0);
  const paymentIdsForSearch = React.useRef<ByMethodPaymentResult[]>([]);

  const loadData = async (page?: number) => {
    if (!currentFormData) return;
    setIsLoading(true);
    try {
      const {
        cloverPointIds,
        isCashPayment,
        isCardPayment,
        isQrPayment,
        isFromCheckingAccount,
        isPreOrder,
        ...consumptionsReportPayload
      } = currentFormData;
      const paymentsMethodsCollection = [isCashPayment, isCardPayment, isQrPayment, isFromCheckingAccount, isPreOrder];

      if (
        (cloverPointIds?.length === 0 && paymentsMethodsCollection.every((method) => method === false)) ||
        (cloverPointIds?.length === 0 && paymentsMethodsCollection.every((method) => method === true))
      ) {
        const summaryResult = await getReportingOrdersSummary(currentFormData);

        setSummary(summaryResult ?? { total_transactions: 0, total_amount: 0 });
      } else {
        if (consumptionsReportPayload.paymentIds && consumptionsReportPayload.paymentIds.length === 0) {
          setPaginationOrders({
            currentPage: 0,
            data: [],
            perPage: 0,
            total: 0,
          });
          setSummary({ total_transactions: 0, total_amount: 0 });
        } else {
          const summaryResult = await getReportingOrdersSummary(currentFormData);
          setSummary(summaryResult ?? { total_transactions: 0, total_amount: 0 });
        }
      }
    } catch (error) {
      console.log('error loadData', error);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadReport = async (): Promise<void> => {
    if (!currentFormData) return;
    setIsLoading(true);
    try {
      if (paymentIdsForSearch.current.length > 0) {
        currentFormData.paymentIds = paymentIdsForSearch.current;
      }
      const result = await getAllReportingOrders(currentFormData, i18n.language);
      if (result) {
        const a = document.createElement('a');
        a.href = window.URL.createObjectURL(
          new Blob([result], {
            type: 'application/octet-stream',
          }),
        );
        a.download = 'reporte_ordenes.xlsx';
        a.click();
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (summary && paginationOrders) {
      setSummary(null);
      setPaginationOrders(null);
    }
    if (paymentIdsForSearch.current.length > 0) {
      paymentIdsForSearch.current = [];
    }
    loadData();
  }, [currentFormData]);

  const handleCollapse = (id: number) => {
    if (openCollapseDetail === id) {
      setOpenCollapseDetail(0);
    } else {
      setOpenCollapseDetail(id);
    }
  };

  const getPaymentMethodText = (order: Order) => {
    if (order.refundOfOrder) {
      return t('consumptionsReport-page-paymentMethod-refund', { orderId: order.refundOfOrder });
    }

    if (order.payment?.isSplitPayment) {
      return parseSplitPaymentText(order.payment.subPayments, t);
    }

    return parsePaymentMethod(t, order);
  };

  const getProductPrice = (orderDetail: OrderDetail): string => {
    if (orderDetail.fullProduct?.isUsedForTomorrowBenefit) {
      const price = Number(orderDetail.price);
      const discountPercentage = Number(orderDetail.tomorrowBenefitDiscountPercentage || 0);
      const totalAmount = (price * (1 - discountPercentage / 100)).toFixed(2);

      return formatMoneyAuto(totalAmount);
    }

    return formatMoneyAuto(orderDetail.price);
  };

  const renderContent = (order: Order, index: number): JSX.Element => {
    return (
      <React.Fragment key={index}>
        <TableRow className={'content-table'}>
          <TableCell component="th" scope="row">
            {order.id}
          </TableCell>
          <TableCell>
            {order.luxuryBox ? `${order.luxuryBox.floor}-${order.luxuryBox.identifierOnFloor}` : '-'}
          </TableCell>
          <TableCell>{order.commerce?.name}</TableCell>
          <TableCell>{formatMoneyAuto(order.total)}</TableCell>
          <TableCell>{parseDateTime(order.localDatetime)}</TableCell>
          <TableCell>{order.guestEmail || '-'}</TableCell>
          <TableCell>{order.phone ? formatPhone(order.phone) : '-'}</TableCell>
          <TableCell>{order.user?.identificationNumber ?? '-'}</TableCell>
          <TableCell>{getPaymentMethodText(order)}</TableCell>
          <TableCell>
            {order?.firstDataPayment?.ccLastFourDigits
              ? `**** **** **** ${order.firstDataPayment.ccLastFourDigits}`
              : order?.cloverDataPayment?.cardLastFourDigits?.trim()
              ? `**** **** **** ${order.cloverDataPayment.cardLastFourDigits}`
              : order?.izipayPayment?.cardNumber
              ? `**** **** **** ${order?.izipayPayment?.cardNumber.substring(
                  order?.izipayPayment?.cardNumber.length - 4,
                )}`
              : 'No Aplica'}
          </TableCell>
          <TableCell>
            {order.event ? `${order.event.name} - ${parseDateTime(order.event.eventStart)}` : 'Sin especificar'}
          </TableCell>
          <TableCell>
            <IconButton aria-label="expand row" size="small" onClick={() => handleCollapse(order.id)}>
              {order.id === openCollapseDetail ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
          <TableCell>
            {order.payment?.isSplitPayment
              ? `${order.payment?.subPayments[0]?.cloverPointPayment?.device?.description}, Serial: ${order.payment?.subPayments[0]?.cloverPointPayment?.device?.serial}`
              : order?.cloverDataPayment?.device
              ? `${order?.cloverDataPayment?.device.description}, Serial: ${order?.cloverDataPayment.device.serial}`
              : order?.fiservQrDataPayment?.device
              ? `${order?.fiservQrDataPayment?.device.description}, Serial: ${order?.fiservQrDataPayment?.device.serial}`
              : order?.checkingAccountPayment?.device
              ? `${order?.checkingAccountPayment?.device.description}, Serial: ${order?.checkingAccountPayment?.device.serial}`
              : 'No Aplica'}
          </TableCell>
          <TableCell>{order.delivery ?? 'No Aplica'}</TableCell>
          <TableCell>
            {order.billingStatusId === OrderBillingStatuses.BillingSuccess
              ? t('consumptionsReport-page-invoice-process-success')
              : '-'}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
            <Collapse in={order.id === openCollapseDetail} timeout="auto" unmountOnExit={true}>
              <Box margin={1}>
                <Typography variant="h6" gutterBottom={true} component="div">
                  {t('consumptionsReport-page-detail')}
                </Typography>
                <Table size="small" aria-label="details">
                  <TableHead className="table-header">
                    <TableRow>
                      <TableCell>{t('consumptionsReport-page-product')}</TableCell>
                      <TableCell>{t('consumptionsReport-page-amount')}</TableCell>
                      <TableCell>{t('consumptionsReport-page-clothingSizeType')}</TableCell>
                      <TableCell>{t('consumptionsReport-page-unitPrice')}</TableCell>
                      <TableCell>{t('consumptionsReport-page-totalProduct')}</TableCell>
                      <TableCell>{t('consumptionsReport-page-isBenefitProduct')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.details.map((detail: OrderDetail) => (
                      <TableRow key={detail.id}>
                        <TableCell component="th" scope="row">
                          {typeof detail.product === 'string' ? detail.product : (detail.product as any)?.name}
                        </TableCell>
                        <TableCell>{detail.quantity}</TableCell>
                        <TableCell>{detail.clothinSizeType?.description ?? '-'}</TableCell>
                        <TableCell>{getProductPrice(detail)}</TableCell>
                        <TableCell>{formatMoneyAuto(detail.totalAmount)}</TableCell>
                        <TableCell>
                          <BooleanIcon value={detail.fullProduct?.isUsedForTomorrowBenefit} showText />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  return (
    <>
      <ConsumptionsReportForm
        currentFormData={currentFormData}
        setCurrentFormData={setCurrentFormData}
        justForDownload={false}
      />

      {/* Tarjetas con la información del Resumen KPI */}
      {summary && (
        <Box marginTop={3} marginBottom={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total de Transacciones
                  </Typography>
                  <Typography variant="h4" component="h2">
                    {summary.total_transactions}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Monto Total
                  </Typography>
                  <Typography variant="h4" component="h2" color="primary">
                    {formatMoneyAuto(summary.total_amount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* 
      <>
        <TableContainer className="table-container" component={Paper}>
          <Table>
            <TableHead className="table-header">
              <TableRow>
                <TableCell>{t('consumptionsReport-page-order')}</TableCell>
                <TableCell>{t('consumptionsReport-page-luxuryBox')}</TableCell>
                <TableCell>{t('consumptionsReport-page-commerce')}</TableCell>
                <TableCell>{t('consumptionsReport-page-total')}</TableCell>
                <TableCell>{t('consumptionsReport-page-createdDate')}</TableCell>
                <TableCell>{t('consumptionsReport-page-guestEmail')}</TableCell>
                <TableCell>{t('consumptionsReport-page-phone')}</TableCell>
                <TableCell>{t('consumptionsReport-page-identificationNumber')}</TableCell>
                <TableCell>{t('consumptionsReport-page-paymentMethod')}</TableCell>
                <TableCell>{t('consumptionsReport-page-lastFourDigits')}</TableCell>
                <TableCell>Evento</TableCell>
                <TableCell>{t('consumptionsReport-page-detail')}</TableCell>
                <TableCell>{t('consumptionsReport-page-clover-point')}</TableCell>
                <TableCell>{t('consumptionsReport-page-delivery')}</TableCell>
                <TableCell>{t('consumptionsReport-page-invoice-process')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginationOrders?.data && paginationOrders?.data.length > 0 ? (
                paginationOrders?.data.map((order: Order, index) => renderContent(order, index))
              ) : (
                <TableRow>
                  <TableCell>{t('consumptionsReport-page-noOperations')}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {paginationOrders && (
          <>
            <TableRow>
              <TableCell>{`${t('consumptionsReport-page-operationsTotal')}: ${
                summary?.total_amount ? formatMoneyAuto(summary.total_amount) : 0
              }`}</TableCell>
            </TableRow>
            <TablePagination
              component="div"
              count={paginationOrders.total}
              page={paginationOrders.currentPage - 1}
              onPageChange={(_, page: number) => {
                loadData(page + 1);
              }}
              rowsPerPage={paginationOrders.perPage}
              rowsPerPageOptions={[]}
              labelDisplayedRows={({ from, to, count }) => {
                return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
              }}
            />
            {paginationOrders?.data.length > 0 && (
              <Box marginTop="1rem">
                <Button
                  className="btn-50"
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={isLoading}
                  onClick={() => {
                    downloadReport();
                  }}
                >
                  {t('consumptionsReport-page-download')}
                </Button>
              </Box>
            )}
          </>
        )}
      </> 
      */}

      {summary !== null ? (
        <Box marginTop="1rem">
          <Button
            className="btn-50"
            variant="contained"
            color="primary"
            type="submit"
            disabled={isLoading}
            onClick={() => {
              downloadReport();
            }}
          >
            {t('consumptionsReport-page-download')}
          </Button>
        </Box>
      ) : null}
    </>
  );
};
